import React, { useState, useCallback } from 'react';
import { ShieldCheck } from 'lucide-react';
import { updateUser } from '../../api/userDataService';

// ⚠️ CONSENT VERSION — READ BEFORE CHANGING ⚠️
// The Terms of Use & Privacy Policy live on the marketing site (routes-app.com), NOT in this app,
// so the app cannot detect when they change. This number is the ONLY trigger that re-prompts users.
// WHENEVER the Terms of Use or Privacy Policy are updated on the website, INCREMENT this number
// (1 -> 2 -> 3 ...) and deploy. Every user will then be asked to re-consent on their next login.
// See CONSENT_GATE.md for the full process.
export const CONSENT_VERSION = 1;

const TERMS_URL = 'https://routes-app.com/terms-of-use/';
const PRIVACY_URL = 'https://routes-app.com/privacy-policy';

// Returns true when the stored user has not yet accepted the current consent version.
// App.js uses this to decide whether to render the gate INSTEAD of the app (so the protected
// routes never mount until consent is given — deleting the popup in devtools just leaves a
// blank screen rather than a usable app).
export const userNeedsConsent = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const consent = user?.consent;
    return !consent?.acceptedAt || consent?.version !== CONSENT_VERSION;
  } catch {
    // If we can't read the user, fail safe by requiring consent.
    return true;
  }
};

/**
 * Blocking consent gate shown on first login (and whenever CONSENT_VERSION changes).
 * Controlled by App.js: it is only mounted when consent is required, and is rendered in
 * place of the app's routes (not as an overlay on top of them).
 * - Accepting records consent on the user document and calls onAccept to unblock the app.
 * - Declining signs the user out via onDecline.
 */
const ConsentGate = ({ onAccept, onDecline }) => {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = useCallback(async () => {
    if (!checked || saving) return;
    setSaving(true);
    setError('');

    const consent = {
      version: CONSENT_VERSION,
      acceptedAt: new Date().toISOString(),
    };

    try {
      await updateUser({ consent });

      // Keep the locally cached user in sync so the gate stays closed on refresh.
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        user.consent = consent;
        localStorage.setItem('user', JSON.stringify(user));
      } catch {
        /* non-fatal — DB is the source of truth */
      }

      if (onAccept) onAccept();
    } catch (err) {
      console.error('Failed to record consent:', err);
      setError('We could not save your response. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [checked, saving, onAccept]);

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-gate-title"
    >
      <div className="bg-white rounded-xl shadow-2xl border-4 border-theme-primary p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-full bg-theme-primary/10 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-theme-primary" />
          </div>
          <h2 id="consent-gate-title" className="text-xl md:text-2xl font-bold text-theme-primary">
            Before you get started
          </h2>
        </div>

        <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-4">
          To use Routes we need your agreement to our terms and to how we handle your data.
          Please review these and confirm your consent to continue.
        </p>

        <div className="flex flex-wrap gap-x-5 gap-y-1 mb-5 text-sm">
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-theme-primary font-medium underline-offset-2 hover:underline"
          >
            Terms of Use
          </a>
          <a
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-theme-primary font-medium underline-offset-2 hover:underline"
          >
            Privacy Policy
          </a>
        </div>

        {/* Input is a SIBLING of the label (not a child) so it does not match the global
            `.dark-mode label:has(input:checked)` rules that add a green left border + background. */}
        <div className="flex items-center gap-3 mb-5">
          <input
            id="consent-accept"
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="h-5 w-5 flex-shrink-0 accent-theme-primary cursor-pointer"
          />
          <label htmlFor="consent-accept" className="text-gray-700 text-sm leading-relaxed cursor-pointer select-none">
            I have read and agree to the{' '}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-theme-primary underline font-medium">
              Terms of Use
            </a>{' '}
            and{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-theme-primary underline font-medium">
              Privacy Policy
            </a>
            , and I consent to Routes using my data as described.
          </label>
        </div>

        {error && (
          <p className="text-red-600 text-sm mb-4" role="alert">
            {error}
          </p>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <button
            type="button"
            onClick={onDecline}
            disabled={saving}
            className="consent-decline-btn flex-1 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            Decline &amp; sign out
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={!checked || saving}
            className="flex-1 px-6 py-3 rounded-lg bg-theme-primary text-white font-semibold shadow-lg hover:bg-theme-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Agree & continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentGate;
